import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Stack,
  Text,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react';
import { faSliders } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@morressier/alembic';
import { UseFormWatch } from 'react-hook-form';
import {
  IMyReviewsFilters,
  IMySubmissionFilter,
  IWorkspaceSubmissionFilter,
  IWorkspaceUserFilter
} from 'src/api/queries';
import FiltersAccordion from './FiltersAccordion';
import { IEarlyAssessmentListFilters } from 'src/pages/MyTask/Assessments/hooks';
import { IAdjudicationsListFilters } from 'src/pages/MyTask/Adjudications/hooks';
import { IListTemplatesQuery } from 'src/api/email-service';
import { useState } from 'react';
import { ITagFilter } from 'src/pages/Organization/Workspace/Settings/ManageTags/ManageTags';

interface FilterGroup {
  key: string;
  label: string;
  items: { value: string; name: string }[];
}

interface Props {
  watch:
    | UseFormWatch<IWorkspaceSubmissionFilter>
    | UseFormWatch<IMySubmissionFilter>
    | UseFormWatch<IMyReviewsFilters>
    | UseFormWatch<IEarlyAssessmentListFilters>
    | UseFormWatch<IAdjudicationsListFilters>
    | UseFormWatch<IWorkspaceUserFilter>
    | UseFormWatch<IListTemplatesQuery>
    | UseFormWatch<ITagFilter>;
  onSubmit: (values: Record<string, string[]>) => void;
  selectedFilters: Record<string, string[]>;
  setSelectedFilters: (filters: Record<string, string[]>) => void;
  filterGroups: FilterGroup[];
}

const FilterDrawer: React.FC<Props> = ({
  onSubmit,
  selectedFilters,
  setSelectedFilters,
  filterGroups
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const drawerSize = useBreakpointValue({ base: 'full', md: 'sm' });

  // Local state to store changes before user confirms
  const [localFilters, setLocalFilters] =
    useState<Record<string, string[]>>(selectedFilters);

  const filterGroupKeys = filterGroups.map(group => group.key);

  // Reset localFilters when opening drawer
  const handleOpen = () => {
    setLocalFilters(selectedFilters);
    onOpen();
  };

  // Reset local state when closing WITHOUT saving
  const handleClose = () => {
    setLocalFilters(selectedFilters); // discard un-applied selections
    onClose();
  };

  // Count only applied filters (from selectedFilters)
  const totalSelected = Object.entries(selectedFilters || {}).reduce(
    (total, [key, value]) => {
      if (!filterGroupKeys.includes(key)) return total;
      return total + (Array.isArray(value) ? value.length : 0);
    },
    0
  );

  return (
    <>
      <Button
        leftIcon={<FontAwesomeIcon icon={faSliders} />}
        variant="secondary"
        onClick={handleOpen}
        width={{ base: '100%', md: 'auto' }}
      >
        Filters {totalSelected > 0 && ` (${totalSelected})`}
      </Button>

      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size={drawerSize}
      >
        <DrawerOverlay />
        <DrawerContent
          display="flex"
          flexDirection="column"
          sx={{ height: { base: '100dvh', md: 'auto' } }}
        >
          <DrawerCloseButton onClick={handleClose} />
          <DrawerHeader></DrawerHeader>

          <DrawerBody px={{ base: 4, md: 6 }} overflowY="auto" flex="1">
            <Stack spacing="15px">
              <Text textStyle="headingItem">
                Selected Filters (
                {
                  Object.entries(localFilters).filter(
                    ([key, value]) =>
                      filterGroupKeys.includes(key) &&
                      Array.isArray(value) &&
                      value.length > 0
                  ).length
                }
                )
              </Text>

              <FiltersAccordion
                filterGroups={filterGroups}
                selectedFilters={localFilters}
                setSelectedFilters={setLocalFilters}
              />
            </Stack>
          </DrawerBody>

          <DrawerFooter
            borderTopWidth="1px"
            px={{ base: 4, md: 6 }}
            pb={{ base: 'max(16px, env(safe-area-inset-bottom))', md: 4 }}
          >
            <Button
              onClick={() => {
                // Apply final changes
                setSelectedFilters(localFilters);
                onSubmit(localFilters);
                onClose();
              }}
              size="fullWidth"
            >
              View Results
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default FilterDrawer;
